import { useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { updateTourCompletion } from "@store/thunks/userProfile";
import { ROUTE_NAMES } from "@root/constants";
import { TourGuideType } from "@root/constants/tour-guide";
import useMediaQ from "@root/hooks/useMediaQ";

/**
 * Tour Guide for Pledge Manage Courses page.
 * Targets search bar and table section. Table may be loading or empty;
 * we target the container which always exists.
 */
export const usePledgeManageCoursesTourGuide = () => {
  const { t } = useTranslation("common");
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { setActiveTourModule, setCurrentTourStep } = useTourGuideContext();
  const { sm } = useMediaQ();
  const isMobile = !sm;

  const isOnManageCourses = pathname === ROUTE_NAMES.PLEDGE_COURSES;

  const config: TourGuideConfig = useMemo(
    () => ({
      moduleId: "pledgeManageCourses",
      enabled: isOnManageCourses,
      steps: [
        {
          id: "pledge-manage-courses-search",
          targetSelector: '[data-tour-item="pledge-manage-courses-search"]',
          title: t("tourGuide.pledgeManageCourses.search.title"),
          description: t("tourGuide.pledgeManageCourses.search.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
        {
          id: "pledge-manage-courses-table",
          targetSelector: '[data-tour-item="pledge-manage-courses-table"]',
          title: t("tourGuide.pledgeManageCourses.table.title"),
          description: t("tourGuide.pledgeManageCourses.table.description"),
          position: "top",
          offset: { x: 0, y: -15 },
          highlightPadding: 12,
        },
      ],
    }),
    [isOnManageCourses, t],
  );

  const baseTourGuide = useTourGuide(config);

  const handleTourComplete = () => {
    dispatch(
      updateTourCompletion({
        payload: {
          tourGuides: [{ type: TourGuideType.PledgeManageCourses, isDone: true }],
        },
        onSuccess: () => {},
        onError: () => {},
      }) as any,
    );
  };

  const pledgeManageCoursesTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourGuideType: TourGuideType.PledgeManageCourses,
    isOnTourPath: isOnManageCourses && !isMobile,
    onTourComplete: handleTourComplete,
  });

  useEffect(() => {
    if (pledgeManageCoursesTourGuide.isActive) {
      setActiveTourModule("pledgeManageCourses");
      setCurrentTourStep(pledgeManageCoursesTourGuide.currentStep);
    } else {
      setActiveTourModule(null);
    }
  }, [
    pledgeManageCoursesTourGuide.isActive,
    pledgeManageCoursesTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
  ]);

  return {
    pledgeManageCoursesTourGuide,
    isMobile,
  };
};
